import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import {
  corporateCharterScheduleSchema,
  CorporateCharterScheduleSchemaType,
  CorporateCharterScheduleStopsSchemaType,
} from 'components/forms/CorporateCharter/types';
import { ControlPoint, DeleteOutline, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { colors, ControlledField, DistanceUnit, TimePickerInputField } from 'remote/global';
import { useOperatorDistanceUnit } from 'api/queries/users';
import { convertDistanceUnit } from 'utils/helpers';

const columns = [
  {
    field: 'stopName',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'address',
    minWidth: 200,
    flex: 1.5,
  },
  {
    field: 'time',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'distance',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'totalTime',
    minWidth: 100,
    flex: 1,
  },
  {
    field: 'totalDistance',
    minWidth: 100,
    flex: 1,
  },
];

function convertMinutes(minutes: number) {
  // Use dayjs to create a duration from minutes
  const duration = dayjs.duration(minutes, 'minutes');

  // Format the duration in HH:mm format
  const formattedTime = dayjs.utc(duration.asMilliseconds()).format('HH:mm');

  return formattedTime;
}

function accumulateFromPrevious(array: CorporateCharterScheduleStopsSchemaType[]) {
  let accumulatedMinutes = 0;
  let accumulatedDistance = 0;

  return array.map((item) => {
    const { minutesFromPrevious, distanceKmFromPrevious } = item;

    // Accumulate
    accumulatedMinutes += minutesFromPrevious || 0;
    accumulatedDistance += distanceKmFromPrevious || 0;

    // Update the item with the accumulated value
    return {
      ...item,
      accumulatedMinutesFromPrevious: accumulatedMinutes,
      accumulatedDistanceFromPrevious: accumulatedDistance,
    };
  });
}

export const getTotalDistance = (itinerary: CorporateCharterScheduleSchemaType, index: number) => {
  const times = accumulateFromPrevious(itinerary.stops);

  const currentValue = times[index].accumulatedDistanceFromPrevious;
  return currentValue;
};

export const getMinutesFromPrevious = (
  itinerary: CorporateCharterScheduleSchemaType,
  index: number
) => {
  const times = accumulateFromPrevious(itinerary.stops);

  const currentValue = times[index].accumulatedMinutesFromPrevious;
  return currentValue;
};
interface TimeFieldProps {
  formInstance: UseFormReturn<CorporateCharterScheduleSchemaType>;
  name: string;
}

function TimeField({ name, formInstance }: TimeFieldProps) {
  const { control } = formInstance;
  return (
    <ControlledField
      control={control}
      name={name as keyof CorporateCharterScheduleSchemaType}
      render={(renderProps) => (
        <TimePickerInputField
          {...renderProps}
          fieldProps={{
            value: dayjs(renderProps.field.value as string, 'HH:mm:ss'),
            onChange: (value) =>
              renderProps.field.onChange(dayjs(value as unknown as string).format('HH:mm:ss')),
          }}
        />
      )}
    />
  );
}

interface Props {
  defaultValues: CorporateCharterScheduleSchemaType;
  onValid: SubmitHandler<CorporateCharterScheduleSchemaType>;
  onBack: (data: CorporateCharterScheduleSchemaType) => void;
}

function ScheduleOverview({ defaultValues, onValid, onBack }: Props) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const distanceUnit = useOperatorDistanceUnit();

  const formInstance = useForm<CorporateCharterScheduleSchemaType>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: zodResolver(corporateCharterScheduleSchema),
  });

  const { control, setValue, getValues, handleSubmit, watch } = formInstance;

  const isReturnTripVisible = watch('departures.0.returnTripVisible');

  const itinerary = getValues();
  if (!defaultValues.stops) return null;
  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Box
        sx={{
          p: '0 24px 24px',
        }}
      >
        <Paper sx={{ width: '100%', background: 'transparent', overflowX: 'auto' }}>
          <Paper elevation={3}>
            <Table
              sx={{
                minWidth: '900px',
                overflow: 'scroll',
                background: 'white',
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    '& > *:first-of-type': {
                      paddingLeft: '16px',
                    },
                    '& > *:last-of-type': {
                      paddingRight: '16px',
                    },
                  }}
                >
                  {columns.map(({ field }) => {
                    return (
                      <TableCell
                        key={field}
                        sx={{
                          fontWeight: 600,
                          padding: '10px 6px',
                          borderBottom: 'none',
                          color: colors.gray2,
                        }}
                      >
                        <span
                          style={{
                            marginRight: '5px',
                          }}
                        >
                          {t(`CorporateCharterView.steps.schedule.table.header.${field}`)}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  '& > tr > td': {
                    borderBottom: 'none',
                    padding: '10px 6px',
                  },
                  '& > tr:nth-of-type(odd)': {
                    backgroundColor: colors.uiBlue50,
                  },
                }}
              >
                {defaultValues.stops.map((stop, index: number) => {
                  return (
                    <TableRow
                      key={stop.location.placeId}
                      sx={{
                        '& > *:first-of-type': {
                          paddingLeft: '16px',
                        },
                        '& > *:last-of-type': {
                          paddingRight: '16px',
                        },
                      }}
                    >
                      <TableCell>
                        <ControlledField control={control} name={`stops.${index}.name`} />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {stop.location.address}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        <TimeField formInstance={formInstance} name={`stops.${index}.time`} />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {/* // TODO: Replace with <ControlledField /> */}
                        {/* COMMENT: if I use controlledfield and press next, then the distanceKmFromPrevious values get set to "undefined" */}
                        <TextField
                          disabled
                          value={convertDistanceUnit(
                            stop.distanceKmFromPrevious,
                            DistanceUnit.KM,
                            distanceUnit
                          )}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {/* // TODO: Replace with <ControlledField /> */}
                        <TextField
                          disabled
                          value={convertMinutes(getMinutesFromPrevious(itinerary, index))}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {/* // TODO: Replace with <ControlledField /> */}
                        <TextField
                          disabled
                          value={convertDistanceUnit(
                            getTotalDistance(itinerary, index),
                            DistanceUnit.KM,
                            distanceUnit
                          ).toFixed(2)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Paper>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                '& > *:first-of-type': {
                  paddingLeft: '16px',
                },
                '& > *:last-of-type': {
                  paddingRight: '16px',
                },
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  padding: '10px 6px',
                  borderBottom: 'none',
                  color: colors.gray2,
                }}
              >
                <span
                  style={{
                    marginRight: '5px',
                  }}
                >
                  {t('CorporateCharterView.steps.schedule.returnTrip.title')}
                </span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow
              sx={{
                background: colors.primaryBlue5,
                p: '10px',
              }}
            >
              <TableCell
                sx={{
                  borderBottom: 'none',
                  fontWeight: 600,
                  padding: '10px 6px',
                  color: colors.gray2,
                  pl: '16px',
                }}
              >
                {isReturnTripVisible ? (
                  <Box sx={{ display: 'flex', maxWidth: '238px' }}>
                    <TimeField formInstance={formInstance} name='departures.0.clientReturnTime' />

                    <IconButton
                      size='large'
                      sx={{
                        padding: 0,
                        color: colors.gray2,
                        ml: '18px',
                        mr: '18px',
                      }}
                      onClick={() => {
                        setValue('departures.0.clientReturnTime', '');
                        setValue('departures.0.returnTripVisible', false);
                      }}
                    >
                      <DeleteOutline color='primary' />
                    </IconButton>
                  </Box>
                ) : (
                  <Button
                    onClick={() => setValue('departures.0.returnTripVisible', true)}
                    startIcon={<ControlPoint />}
                    endIcon={<ExpandMore />}
                  >
                    {t('CorporateCharterView.steps.schedule.returnTrip.actions.addReturn')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Box
          display='flex'
          alignItems='center'
          width='100%'
          m='24px 0'
          justifyContent='space-between'
          flexDirection={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <Button
            sx={{
              mr: {
                xs: 0,
                sm: '24px',
              },
              mb: {
                xs: '12px',
                sm: 0,
              },
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
            variant='outlined'
            onClick={() => onBack(getValues())}
          >
            {t('back')}
          </Button>
          <Button
            disabled={formInstance.formState.isSubmitting}
            type='submit'
            sx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
          >
            {t('continue')}
          </Button>
        </Box>
      </Box>
    </form>
  );
}

export default ScheduleOverview;
